import prefixEve from 'src/utils/routeFilter.js'
import { getFirstConfigPath } from 'src/utils/routePermission.js'
import { useCounterStore } from '@/stores/routerProcessing'
const prefix = prefixEve.cuttingFiler()

// 企业管理入口落地目标：权限过滤后路由表中第一个可见配置页（meta.config）。
// 子账户可能无企业概览权限，不能写死 overview/index；无任何配置权限时回退原地址
// （此时侧边栏入口隐藏，仅直接输地址才会走到，404 与越权访问其它页面口径一致）。
// useCounterStore 在 redirect 函数体内调用：模块间存在 store → 本路由文件的引用，
// 延迟到导航时取值（pinia 已激活）避免循环依赖初始化问题。
const manageEntryRedirect = () =>
  getFirstConfigPath(useCounterStore().routerList) || `${prefix}/overview/index`

// 注意：
// 1. 每个一级菜单一个顶层路由对象（name 唯一），component 复用 home.vue；侧边栏分组标题由一级菜单 meta.group 提供；
// 2. 单页模块列表 path 加一层（/index、/list）；quotas / usage 保留 tabs 外壳（/interfaces、/members 等）；
// 3. meta.config 标记「企业管理配置」类菜单：命中时侧边栏切到配置视图（第二种形态）；
// 4. meta.title 同时是侧边栏菜单文案与面包屑来源（tabs 子页 title 不带父级前缀，页面内 tabs 已表达层级）；
// 5. meta.icon 使用 iconfont class（src/styles/font_5237464_51of7hpp79e，以 icon- 开头）。
// 6. rouKey: 只能手动增加和修改，切记。
// enterprise 端 = 企业版（企业租户控制台）
export const enterpriseRoutes = [
  // ===== 首页 =====
  {
    path: prefix + '/dashboard',
    name: 'EnterpriseLayoutDashboard',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/dashboard/index`,
    meta: { hidden: true },
    children: [
      {
        path: 'index',
        name: 'EnterpriseDashboard',
        component: () => import('@/views/enterprise/dashboard/index.vue'),
        meta: { title: '首页', icon: 'icon-shouye', group: '工作台' },
      }
    ]
  },
  // ===== 应用中心 =====
  {
    path: prefix + '/apps',
    name: 'EnterpriseLayoutApps',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/apps/list`,
    meta: { hidden: true },
    children: [
      {
        path: 'list',
        name: 'EnterpriseApps',
        component: () => import('@/views/enterprise/apps/index.vue'),
        meta: { title: '应用中心', icon: 'icon-yingyongchengxu-xianxing', group: '工作台' },
        rouKey: 'ent.app'
      }
    ]
  },
  // ===== 能力与接入（tabs 外壳 + 能力接口/接入指南两个子页） =====
  {
    path: prefix + '/quotas',
    name: 'EnterpriseLayoutQuotas',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/quotas/interfaces`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseQuotas',
        component: () => import('@/views/enterprise/quotas/index.vue'),
        redirect: `${prefix}/quotas/interfaces`,
        meta: { title: '能力中心', icon: 'icon-gongxiangwangluojieru', group: '工作台' },
        children: [
          {
            path: 'interfaces',
            name: 'EnterpriseQuotasInterfaces',
            component: () => import('@/views/enterprise/quotas/interfaces/index.vue'),
            meta: { title: '能力接口', hidden: true, activeMenu: prefix + '/quotas' },
          },
          {
            path: 'guide',
            name: 'EnterpriseQuotasGuide',
            component: () => import('@/views/enterprise/quotas/guide/index.vue'),
            meta: { title: '接入指南', hidden: true, activeMenu: prefix + '/quotas' },
          }
        ]
      }
    ]
  },
  // ===== 我的用量（单页：积分消耗明细） =====
  {
    path: prefix + '/usage',
    name: 'EnterpriseLayoutUsage',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/usage/index`,
    meta: { hidden: true },
    children: [
      {
        path: 'index',
        name: 'EnterpriseUsage',
        component: () => import('@/views/enterprise/usage/index.vue'),
        meta: { title: '我的用量', icon: 'icon-shiyongliang', group: '工作台', keepAlive: true },
      }
    ]
  },
  // ===== 积分消耗详情（我的用量列表点击进入，不进侧边栏） =====
  {
    path: prefix + '/usage-detail',
    name: 'EnterpriseLayoutUsageDetail',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/usage-detail`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseUsageDetail',
        component: () => import('@/views/enterprise/usage/detail/index.vue'),
        meta: { title: '我的用量/积分消耗详情', hidden: true, activeMenu: prefix + '/usage', cacheParent: 'EnterpriseUsage' },
      }
    ]
  },
  // ===== 我的 Key =====
  {
    path: prefix + '/secret-key',
    name: 'EnterpriseLayoutSecretKey',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/secret-key/index`,
    meta: { hidden: true },
    children: [
      {
        path: 'index',
        name: 'EnterpriseSecretKey',
        component: () => import('@/views/enterprise/keys/index.vue'),
        meta: {
          title: '我的 Key',
          icon: 'icon-miyuefuwu',
          group: '工作台'
        },
      }
    ]
  },
  // ===== 企业概览（归属「企业管理」分组，置于首位） =====
  {
    path: prefix + '/overview',
    name: 'EnterpriseLayoutOverview',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/overview/index`,
    meta: { hidden: true },
    children: [
      {
        path: 'index',
        name: 'EnterpriseOverview',
        component: () => import('@/views/enterprise/overview/index.vue'),
        meta: { title: '企业概览', icon: 'icon-yibiaopan', group: '企业管理', config: true },
        rouKey: 'ent.ent'
      }
    ]
  },
  // ===== 企业用量（配置类，全企业成员积分消耗明细，置于企业概览之后） =====
  {
    path: prefix + '/enterprise-usage',
    name: 'EnterpriseLayoutEntUsage',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/enterprise-usage/index`,
    meta: { hidden: true },
    children: [
      {
        path: 'index',
        name: 'EnterpriseEntUsage',
        component: () => import('@/views/enterprise/enterprise-usage/index.vue'),
        meta: { title: '企业用量', icon: 'icon-shiyongliang', group: '企业管理', config: true, keepAlive: true },
        rouKey: 'ent.usage'
      }
    ]
  },
  // ===== 企业用量-用量详情（复用工作台「我的用量」详情模板，不进侧边栏） =====
  {
    path: prefix + '/enterprise-usage-detail',
    name: 'EnterpriseLayoutEntUsageDetail',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/enterprise-usage-detail`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseEntUsageDetail',
        component: () => import('@/views/enterprise/usage/detail/index.vue'),
        meta: { title: '企业用量/用量详情', hidden: true, config: true, activeMenu: prefix + '/enterprise-usage/index', cacheParent: 'EnterpriseEntUsage' },
      }
    ]
  },
  // ===== 积分余额（配置类，置于企业用量之后） =====
  {
    path: prefix + '/billing',
    name: 'EnterpriseLayoutBilling',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/billing/index`,
    meta: { hidden: true },
    children: [
      {
        path: 'index',
        name: 'EnterpriseBilling',
        component: () => import('@/views/enterprise/billing/index.vue'),
        meta: { title: '积分余额', icon: 'icon-jifen', group: '企业管理', config: true, keepAlive: true },
        rouKey: 'ent.balance'
      }
    ]
  },
  // ===== 积分余额-积分包详情（列表进入，不进侧边栏） =====
  {
    path: prefix + '/billing-detail',
    name: 'EnterpriseLayoutBillingDetail',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/billing-detail`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseBillingDetail',
        component: () => import('@/views/enterprise/billing/detail/index.vue'),
        // keepQuery：面包屑「积分包详情」层级回跳时保留 id，避免退回到第一个积分包
        meta: {
          title: '积分余额/积分包详情',
          hidden: true,
          config: true,
          activeMenu: prefix + '/billing/index',
          keepQuery: ['id'],
          keepAlive: true,
          cacheParent: 'EnterpriseBilling'
        },
      }
    ]
  },
  // ===== 积分余额-用量详情（积分包详情用量明细进入，不进侧边栏；复用用量详情模板） =====
  {
    path: prefix + '/billing-usage-detail',
    name: 'EnterpriseLayoutBillingUsageDetail',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/billing-usage-detail`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseBillingUsageDetail',
        component: () => import('@/views/enterprise/usage/detail/index.vue'),
        meta: {
          title: '积分余额/积分包详情/用量详情',
          hidden: true,
          config: true,
          activeMenu: prefix + '/billing/index',
          cacheParent: 'EnterpriseBillingDetail'
        },
      }
    ]
  },
  // ===== 积分余额-在线充值（列表「在线储值」进入，不进侧边栏） =====
  {
    path: prefix + '/billing-topup',
    name: 'EnterpriseLayoutBillingTopup',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/billing-topup`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseBillingTopup',
        component: () => import('@/views/enterprise/billing/topup/index.vue'),
        meta: { title: '积分余额/在线充值', hidden: true, config: true, activeMenu: prefix + '/billing/index', cacheParent: 'EnterpriseBilling' },
      }
    ]
  },
  // ===== 企业管理（配置视图入口，redirect 动态落到权限内第一个配置页） =====
  // 函数形态 redirect 被 isRedirectAlive 视为存活：入口不再因子账户无企业概览权限被整棵剔除
  {
    path: prefix + '/manage',
    name: 'EnterpriseLayoutManage',
    component: () => import('@/views/home.vue'),
    redirect: manageEntryRedirect,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseManage',
        redirect: manageEntryRedirect,
        meta: { title: '企业管理', icon: 'icon-qiyeguanli', group: '企业管理' },
      }
    ]
  },
  // ===== 财务中心-账务明细（配置类，二级菜单：meta.parent 聚合为 el-sub-menu） =====
  {
    path: prefix + '/finance',
    name: 'EnterpriseLayoutFinance',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/finance/records`,
    meta: { hidden: true },
    children: [
      {
        path: 'records',
        name: 'EnterpriseFinanceRecords',
        component: () => import('@/views/enterprise/finance/records/index.vue'),
        meta: {
          title: '账务明细',
          group: '企业管理',
          config: true,
          parent: '财务中心',
          parentIcon: 'icon-chuzhiqia',
          keepAlive: true
        },
        rouKey: 'ent.billing.recharge'
      }
    ]
  },
  // ===== 财务中心-交易详情（账务明细进入，不进侧边栏） =====
  {
    path: prefix + '/finance-tx-detail',
    name: 'EnterpriseLayoutFinanceTxDetail',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/finance-tx-detail`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseFinanceTxDetail',
        component: () => import('@/views/enterprise/finance/records/detail/index.vue'),
        meta: { title: '账务明细/交易详情', hidden: true, config: true, activeMenu: prefix + '/finance/records', cacheParent: 'EnterpriseFinanceRecords' },
      }
    ]
  },
  // ===== 部门管理（配置类） =====
  {
    path: prefix + '/departments',
    name: 'EnterpriseLayoutDepartments',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/departments`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseDepartments',
        component: () => import('@/views/enterprise/departments/index.vue'),
        meta: {
          title: '部门管理',
          icon: 'icon-bumenguanli',
          group: '企业管理',
          config: true,
          parent: '组织管理',
          parentIcon: 'icon-yonghu'
        },
        rouKey: 'ent.user.department'
      }
    ]
  },
  // ===== 角色管理（配置类） =====
  {
    path: prefix + '/roles',
    name: 'EnterpriseLayoutRoles',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/roles`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseRoles',
        component: () => import('@/views/enterprise/organization/roles/index.vue'),
        meta: {
          title: '角色管理',
          icon: 'icon-jiaoseguanli',
          group: '企业管理',
          config: true,
          parent: '组织管理'
        },
        rouKey: 'ent.user.role'
      }
    ]
  },
  // ===== 职工管理（配置类） =====
  {
    path: prefix + '/staff',
    name: 'EnterpriseLayoutStaff',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/staff`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseStaff',
        component: () => import('@/views/enterprise/organization/staff/index.vue'),
        meta: {
          title: '职工管理',
          icon: 'icon-yonghu',
          group: '企业管理',
          config: true,
          parent: '组织管理'
        },
        rouKey: 'ent.user.user'
      }
    ]
  },
  // ===== 个人中心（右上角头像菜单进入，不进侧边栏） =====
  {
    path: prefix + '/profile',
    name: 'EnterpriseLayoutProfile',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/profile`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseProfile',
        component: () => import('@/views/enterprise/profile/index.vue'),
        meta: { title: '个人中心', hidden: true },
      }
    ]
  },
  // ===== 系统配置（配置类，二级菜单：企业信息 / 操作日志） =====
  {
    path: prefix + '/system',
    name: 'EnterpriseLayoutSystem',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/system/info`,
    meta: { hidden: true },
    children: [
      {
        path: 'info',
        name: 'EnterpriseSystemInfo',
        component: () => import('@/views/enterprise/system/info/index.vue'),
        meta: {
          title: '企业信息',
          group: '企业管理',
          config: true,
          parent: '系统配置',
          parentIcon: 'icon-xitongguanli'
        },
        rouKey: 'ent.setting.ent_info'
      },
      {
        path: 'logs',
        name: 'EnterpriseSystemLogs',
        component: () => import('@/views/enterprise/system/logs/index.vue'),
        meta: {
          title: '操作日志',
          group: '企业管理',
          config: true,
          parent: '系统配置',
          keepAlive: true
        },
        rouKey: 'ent.setting.log'
      }
    ]
  },
  // ===== 系统配置-日志详情（操作日志进入，不进侧边栏） =====
  {
    path: prefix + '/system-log-detail',
    name: 'EnterpriseLayoutSystemLogDetail',
    component: () => import('@/views/home.vue'),
    redirect: `${prefix}/system-log-detail`,
    meta: { hidden: true },
    children: [
      {
        path: '',
        name: 'EnterpriseSystemLogDetail',
        component: () => import('@/views/enterprise/system/logs/detail/index.vue'),
        meta: { title: '操作日志/日志详情', hidden: true, config: true, activeMenu: prefix + '/system/logs', cacheParent: 'EnterpriseSystemLogs' },
      }
    ]
  }
]

export default enterpriseRoutes
